
  import {
    defineComponent,
    ref,
    onMounted,
    computed,
    onBeforeUnmount,
  } from 'vue';
  import { useRouter } from 'vue-router';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useAbility } from '@casl/vue';
  import Datatable from '@/components/kt-datatable/KTDatatable.vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import PropertiesAdvancedSearch from '@/components/search/PropertiesAdvancedSearch.vue';

  export default defineComponent({
    name: 'auctions-properties-listing',
    components: {
      Datatable,
      PropertiesAdvancedSearch,
    },
    async setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const { can } = useAbility();

      const translate = (text) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      const formatCurrency = (amount) => {
        if (!amount) return 'N/A';
        return new Intl.NumberFormat('en-KW', {
          style: 'currency',
          currency: 'KWD',
        }).format(amount);
      };

      const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-KW');
      };

      onMounted(() => {
        setCurrentPageBreadcrumbs(translate('propertiesListing'), [
          translate('auctions'),
        ]);
      });

      onBeforeUnmount(() => {
        store.dispatch(Actions.RESET_AUCTIONS_PROPERTIES_LIST);
      });

      await store.dispatch(Actions.GET_AUCTION_PROPERTIES_LISTING);
      const loading = ref(false);

      const tableHeader = ref([
        {
          name: translate('status'),
          key: 'isActive',
          sortable: false,
        },
        {
          name: translate('actions'),
          key: 'actions',
        },
        {
          name: translate('id'),
          key: 'id',
          sortable: true,
        },
        {
          name: translate('propertyId'),
          key: 'propertyId',
          sortable: true,
        },
        {
          name: translate('block'),
          key: 'block',
          sortable: true,
        },
        {
          name: translate('plot'),
          key: 'plot',
          sortable: true,
        },
        {
          name: translate('paciNo'),
          key: 'paciNo',
          sortable: true,
        },
        {
          name: translate('size'),
          key: 'size',
          sortable: true,
        },
        {
          name: translate('startingPrice'),
          key: 'startingPrice',
          sortable: true,
        },
        {
          name: translate('startingPricePerSqrM'),
          key: 'startingPricePerSqrM',
          sortable: true,
        },
        {
          name: translate('salePrice'),
          key: 'salePrice',
          sortable: true,
        },
        {
          name: translate('salePricePerSqrM'),
          key: 'salePricePerSqrM',
          sortable: true,
        },
        {
          name: translate('paidPercentage'),
          key: 'paidPercentage',
          sortable: true,
        },
        {
          name: translate('premiumPaid'),
          key: 'percentagePremiumPaid',
          sortable: true,
        },
        {
          name: translate('createdDate'),
          key: 'createdAt',
          sortable: true,
        },
      ]);

      const viewPropertyOverview = async (id) => {
        router.push({ name: 'auctions-properties-overview', params: { id } });
        await store.dispatch(Actions.ADD_BODY_CLASSNAME, 'page-loading-over');
      };

      const updateProperty = async (id) => {
        router.push({ name: 'auctions-properties-editing', params: { id } });
        await store.dispatch(Actions.ADD_BODY_CLASSNAME, 'page-loading-over');
      };

      const toggleStatusProperty = async (property) => {
        const statusMessage = property.isActive
          ? 'confirmInActive'
          : 'confirmActive';
        const result = await Swal.fire({
          title: translate('areYouSureQ'),
          icon: 'warning',
          text: translate(statusMessage),
          showCancelButton: true,
          buttonsStyling: false,
          confirmButtonText: translate('confirm'),
          cancelButtonText: translate('cancel'),
          customClass: {
            confirmButton: 'btn fw-bold btn-warning',
            cancelButton: 'btn btn-light',
          },
        });

        if (result.value) {
          loading.value = true;
          // TODO: Add action to update property status
          // await store.dispatch(Actions.UPDATE_PROPERTY_STATUS, property.id);
          await store.dispatch(Actions.GET_AUCTION_PROPERTIES_LISTING);
          loading.value = false;
          const [errorName] = Object.keys(store.getters.getErrors);
          const error = store.getters.getErrors[errorName];
          if (error) {
            Swal.fire({
              text: translate(error[0]),
              icon: 'error',
              buttonsStyling: false,
              confirmButtonText: translate('tryAgainExcl'),
              customClass: {
                confirmButton: 'btn fw-bold btn-light-danger',
              },
            });
          } else {
            const successMsg = property.isActive
              ? 'SUCCESSFULLY_INACTIVATED'
              : 'SUCCESSFULLY_ACTIVEATED';
            Swal.fire({
              text: translate(successMsg),
              icon: 'success',
              buttonsStyling: false,
              confirmButtonText: translate('ok'),
              customClass: {
                confirmButton: 'btn btn-light-primary',
              },
            });
          }
        }
      };

      const goTo = (pageName) => {
        router.push({ name: pageName });
      };

      const pageChanged = async (val) => {
        loading.value = true;
        await store.dispatch(Actions.UPDATE_AUCTION_PROPERTY_CURRENT_PAGE, val);
        await store.dispatch(Actions.GET_AUCTION_PROPERTIES_LISTING);
        loading.value = false;
      };

      const onTypeChange = async (value) => {
        loading.value = true;
        await store.dispatch(Actions.UPDATE_AUCTION_PROPERTIES_FILTER, value);
        await store.dispatch(Actions.UPDATE_AUCTION_CURRENT_PAGE, 1);
        await store.dispatch(Actions.GET_AUCTION_PROPERTIES_LISTING);
        loading.value = false;
      };

      const onReset = async (value) => {
        loading.value = true;
        await store.dispatch(Actions.UPDATE_AUCTION_PROPERTIES_FILTER, value);
        await store.dispatch(Actions.GET_AUCTION_PROPERTIES_LISTING);
        loading.value = false;
      };

      const editSeo = async (id) => {
        router.push({
          name: 'seo-configuration',
          params: { entityType: 'auctions-properties', entityId: id },
        });
        await store.dispatch(Actions.ADD_BODY_CLASSNAME, 'page-loading-over');
      };

      store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');

      return {
        tableData: computed(() => store.getters.auctionsPropertiesList || []),
        tableHeader,
        toggleStatusProperty,
        translate,
        totalItems: computed(
          () => store.getters.auctionsPropertiesListCount || 0
        ),
        pageSize: computed(() => store.getters.auctionPropertiesPageSize || 10),
        pageChanged,
        viewPropertyOverview,
        goTo,
        loading,
        can,
        updateProperty,
        formatCurrency,
        formatDate,
        editSeo,
        onTypeChange,
        onReset,
      };
    },
  });
